import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Api from '../../../services';
import { Banner } from '../../../types/banner';
import HeroSkeleton from './hero-skeleton';

export default function Hero(): JSX.Element {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [imageLoaded, setImageLoaded] = useState<boolean[]>([]);
  const { i18n } = useTranslation();

  const isRtl = i18n.language === 'ar';

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await Api.banners.getBanners();
        setBanners(response.data || []);
        setImageLoaded(new Array((response.data || []).length).fill(false));
      } catch (error) {
        setBanners([]);
        setImageLoaded([]);
      }
    };
    fetchBanners();
  }, []);

  const goToPrevious = (): void => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? (banners.length - 1) : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = useCallback((): void => {
    const isLastSlide = currentIndex === banners.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, banners.length]);

  const goToSlide = (slideIndex: number): void => {
    setCurrentIndex(slideIndex);
  };

  useEffect(() => {
    if (!banners.length) return;
    const slideInterval = setInterval(goToNext, 5000);
    return () => clearInterval(slideInterval);
  }, [currentIndex, goToNext, banners.length]);

  const handleImageLoad = (index: number) => {
    setImageLoaded((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  };

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className='relative w-[95%] h-[250px] sm:h-[400px] lg:h-[600px] mx-auto overflow-hidden rounded-2xl mt-6'
    >
      <button
        type='button'
        onClick={goToPrevious}
        className={`absolute top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white ${
          isRtl ? 'right-4' : 'left-4'
        }`}
        aria-label='Previous Slide'
      >
        {isRtl ? <ChevronRightIcon className='w-6 h-6' /> : <ChevronLeftIcon className='w-6 h-6' />}
      </button>
      <button
        type='button'
        onClick={goToNext}
        className={`absolute top-1/2 -translate-y-1/2 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white ${
          isRtl ? 'left-4' : 'right-4'
        }`}
        aria-label='Next Slide'
      >
        {isRtl ? <ChevronLeftIcon className='w-6 h-6' /> : <ChevronRightIcon className='w-6 h-6' />}
      </button>
      <div
        className='flex w-full h-full transition-transform duration-700 ease-in-out'
        style={{ transform: `translateX(${isRtl ? '' : '-'}${currentIndex * 100}%)` }}
      >
        {banners.map((banner, index) => {
          const imageUrl = banner.attributes.logo.url;
          const { link } = banner.attributes;
          const imgElement = (
            <img
              src={imageUrl}
              alt={`banner-${index}`}
              className='w-full h-full object-cover'
              style={{ display: imageLoaded[index] ? 'block' : 'none' }}
              onLoad={() => handleImageLoad(index)}
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                setImageLoaded((prev) => {
                  const updated = [...prev];
                  updated[index] = true;
                  return updated;
                });
              }}
            />
          );
          return (
            <div key={banner.id} className='w-full h-full flex-shrink-0 relative'>
              {link ? (
                <a href={link} target='_blank' rel='noopener noreferrer'>
                  {!imageLoaded[index] && <HeroSkeleton />}
                  {imgElement}
                  <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent' />
                </a>
              ) : (
                <>
                  {!imageLoaded[index] && <HeroSkeleton />}
                  {imgElement}
                  <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent' />
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className='absolute bottom-5 left-1/2 -translate-x-1/2 flex space-x-2 rtl:space-x-reverse'>
        {banners.map((banner, slideIndex) => (
          <button
            type='button'
            key={banner.id}
            onClick={() => goToSlide(slideIndex)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentIndex === slideIndex ? 'bg-white scale-125' : 'bg-white/50'
            }`}
            aria-label={`Go to slide ${slideIndex + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
